import { ValueInPropertiesOrErrorsHasChanged } from "../../utils/ShouldUpdate/ValueInPropertiesHasChanged";
import InternalLink from "../Navigation/InternalLink";
import * as React from "react";
import { BaseComponent } from "components/BaseComponent/BaseComponent";
import FeedResource, {BuiltInFeedId, FeedType} from "client/resources/feedResource";
import { Callout, CalloutType } from "components/Callout";
import Note from "components/form/Note/Note";
import { BoundSelect } from "components/form/Select/Select";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";
import routeLinks from "../../routeLinks";
import AutoComplete, {AutoCompleteOption} from "components/AutoComplete/AutoComplete";
import {repository} from "clientInstance";
import { BoundAutoComplete } from "components/AutoComplete";

const StringProperties = {
    "Octopus.Action.Package.FeedId": "",
    "Octopus.Action.Package.PackageId": "",
};

export type PackageSelectorProperties = {[P in keyof typeof StringProperties]: string };

interface PackageSelectorProps {
    projectId: string;
    feeds: FeedResource[];
    localNames: string[];
    feedType?: FeedType[];
    showBuiltIn?: boolean;
    autoFocus?: boolean;
    properties: PackageSelectorProperties;
    errors: {};
    getFieldError(field: string): string;
    setProperties(properties: Partial<PackageSelectorProperties>): void;
}

interface PackageSelectorState {
    looksLikeVersion: boolean;
    feedType: FeedType[];
    showBuiltIn: boolean;
}

export default class PackageSelector extends BaseComponent<PackageSelectorProps, PackageSelectorState> {
    constructor(props: PackageSelectorProps) {
        super(props);

        const feedType = props.feedType
            ? props.feedType
            : [FeedType.Nuget, FeedType.GitHub];

        this.state = {
            feedType,
            looksLikeVersion: false,
            showBuiltIn: !!props.showBuiltIn
        };
    }

    shouldComponentUpdate(nextProps: PackageSelectorProps) {
        return ValueInPropertiesOrErrorsHasChanged(StringProperties, nextProps, this.props) || nextProps.feeds !== this.props.feeds;
    }

    componentDidMount() {
        if (!this.props.properties["Octopus.Action.Package.FeedId"]) {
            this.props.setProperties({ ["Octopus.Action.Package.FeedId"]: BuiltInFeedId });
        }
    }

    checkIfLooksLikeVersion = () => {
        let looksLikeVersion = false;
        const packageId = this.props.properties["Octopus.Action.Package.PackageId"];
        if (packageId) {
            if (packageId.match(/\.[0-9]+\.?$/)) {
                looksLikeVersion = true;
            }
        }

        this.setState({ looksLikeVersion });
    }

    render() {
        const properties = this.props.properties;
        const feeds = this.props.feeds.filter(f =>
            this.state.feedType.some(feedType => feedType === f.FeedType) ||
            (this.state.showBuiltIn && f.Id === "feeds-builtin"));

        if (feeds.length === 0) {
            return <Callout type={CalloutType.Warning} title="Package feed required">
                A package feed is required before this step can be configured. Please add a feed in
                 the <InternalLink to={routeLinks.library.feeds}>External feeds</InternalLink> area and try again.
            </Callout>;
        }

        const defaultFeedId = this.defaultFeedId(feeds);
        let selectedFeedId = this.getFeedId(feeds);

        if (selectedFeedId === null) {
            selectedFeedId = defaultFeedId;
        }

        const selectedFeed: FeedResource = this.getSelectedFeed(feeds);

        return <div>
            <BoundSelect
                variableLookup={{
                    localNames: this.props.localNames,
                    projectId: this.props.projectId
                }}
                resetValue={defaultFeedId}
                value={selectedFeedId}
                onChange={this.feedChanged}
                items={feeds.map(f => ({ value: f.Id, text: f.Name }))}
                error={this.props.getFieldError("Octopus.Action.Package.FeedId")}
                autoFocus={this.props.autoFocus}
                label="Package feed" />
            <Note>Select the feed that this package will be found in or bind one dynamically. See
                    our <ExternalLink href="DynamicPackageFeeds">documentation</ExternalLink> for more info on dynamic binding.</Note>

            <BoundAutoComplete
                variableLookup={{
                    localNames: this.props.localNames,
                    projectId: this.props.projectId
                }}
                resetValue=""
                label="Package ID"
                value={properties["Octopus.Action.Package.PackageId"] || ""}
                onChange={this.packageChanged}
                hintText="Enter package ID"
                allowAnyTextValue={true}
                getOptions={searchText => this.getPackageOptions(searchText, selectedFeed)}
                error={this.props.getFieldError("Octopus.Action.Package.PackageId")} />
            <Note>
                Enter the ID of the package to deploy.
                {selectedFeed && selectedFeed.FeedType === FeedType.Maven && <span> The format of the package ID is <em>Group:Artifact</em> for
                example <em>com.google.guava:guava</em> or <em>junit:junit</em>.</span>}
            </Note>

            {this.state.looksLikeVersion && <Callout type={CalloutType.Information} title="Package version not required">
                Hey there! It looks like you might be including the package version number in this field. You shouldn't include the package version number here,
                    just the package ID.
                </Callout>}
        </div>;
    }

    private getPackageOptions = async (searchText: string, feed: FeedResource) => {
        if (feed === null) {
            const items: AutoCompleteOption[] = [];
            return {
                items,
                containsAllResults: true
            };
        }

        const packages = await repository.Feeds.searchPackages(feed, {term: searchText, take: 11, skip: 0});
        return {
            items: packages.Items.slice(0, 10).map(p => ({Id: p.Id, Name: p.Id})),
            containsAllResults: packages.Items.length !== 11
        };
    }

    private packageChanged = (packageId: string) => {
        this.props.setProperties({ ["Octopus.Action.Package.PackageId"]: packageId });
        this.checkIfLooksLikeVersion();
    }

    private feedChanged = (feedId: string) => {
        this.props.setProperties({ ["Octopus.Action.Package.FeedId"]: feedId });
    }

    private getFeedId = (feeds: FeedResource[]) => {
        if (this.props.properties["Octopus.Action.Package.FeedId"] !== null) {
            return this.props.properties["Octopus.Action.Package.FeedId"];
        }

        return null;
    }

    private defaultFeedId = (feeds: FeedResource[]) => {
        if (feeds.length === 0) {
            return null;
        }

        const buildtIn = feeds.find(f => f.Id === BuiltInFeedId);

        if (buildtIn) {
            return buildtIn.Id;
        }

        return feeds[0].Id;
    }

    private getSelectedFeed = (feeds: FeedResource[]) => {
        if (feeds.length === 0) {
            return null;
        }

        if (this.props.properties["Octopus.Action.Package.FeedId"]) {
            const feedId = this.props.properties["Octopus.Action.Package.FeedId"];
            const feed = feeds.find(f => f.Id === feedId);

            if (feed) {
                return feed;
            }
        }

        return null;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/PackageSelector/PackageSelector.tsx